import * as z from 'zod/mini'
import { VALIDATION_SK } from './validation.sk'

const REQUIRED = VALIDATION_SK['validation.required']
const INVALID = 'Neplatná hodnota'
const INVALID_DATE = 'Neplatný dátum'

const plural = new Intl.PluralRules('sk')

/** `n` with the Slovak noun form it takes: `countSk(2, ['znak', 'znaky', 'znakov'])` is "2 znaky". */
export const countSk = (n: number | bigint, [one, few, other]: readonly [string, string, string]): string => {
  const category = plural.select(Number(n))
  return `${n} ${category === 'one' ? one : category === 'few' ? few : other}`
}

const CHARACTERS = ['znak', 'znaky', 'znakov'] as const
const ITEMS = ['položku', 'položky', 'položiek'] as const

const isEmpty = (input: unknown) => input === undefined || input === null || input === ''

const FORMATS: Partial<Record<string, string>> = {
  email: 'Zadajte platný e-mail',
  datetime: INVALID_DATE,
  date: INVALID_DATE,
  time: 'Neplatný čas',
}

/**
 * Seiri's Slovak wording of Zod's built-in issues - the replacement for `zod/locales`' `sk()`, whose
 * messages name the type and the operator ("string musí mať >=2 znakov"). Each message is one short
 * line a user can act on, and an empty value always reads as "Povinné pole", whichever check caught
 * it. A wire issue (`describeIssue`) carries no `input`, so it is worded as a non-empty value would be.
 *
 * Covers the codes and formats the schemas in `@seiri/shared/common` emit; a new one gets its own
 * branch here, or falls through to the generic "Neplatná hodnota".
 */
export const localeErrorSk = (issue: z.core.$ZodRawIssue): string => {
  switch (issue.code) {
    case 'invalid_type':
      if (isEmpty(issue.input) || issue.expected === 'nonoptional') return REQUIRED
      if (issue.expected === 'int') return 'Zadajte celé číslo'
      if (issue.expected === 'number') return 'Zadajte číslo'
      if (issue.expected === 'date') return INVALID_DATE
      return INVALID

    case 'too_small':
      if (issue.origin === 'string') {
        return issue.input === '' ? REQUIRED : `Zadajte aspoň ${countSk(issue.minimum, CHARACTERS)}`
      }
      if (issue.origin === 'array' || issue.origin === 'set') return `Vyberte aspoň ${countSk(issue.minimum, ITEMS)}`
      if (issue.origin === 'date') return 'Dátum je príliš skorý'
      return issue.inclusive === false ? `Musí byť väčšie ako ${issue.minimum}` : `Najmenej ${issue.minimum}`

    case 'too_big':
      if (issue.origin === 'string') return `Najviac ${countSk(issue.maximum, CHARACTERS)}`
      if (issue.origin === 'array' || issue.origin === 'set') return `Najviac ${countSk(issue.maximum, ITEMS)}`
      if (issue.origin === 'date') return 'Dátum je príliš neskorý'
      return issue.inclusive === false ? `Musí byť menšie ako ${issue.maximum}` : `Najviac ${issue.maximum}`

    case 'invalid_format':
      if (issue.input === '') return REQUIRED
      return FORMATS[issue.format] ?? 'Neplatný formát'

    case 'invalid_value':
      // Not `isEmpty`: a wire issue has no `input`, and an unknown enum value is not a missing one.
      return issue.input === '' || issue.input === null ? REQUIRED : 'Vyberte jednu z možností'

    default:
      return INVALID
  }
}
